import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { InvocationResponse } from '@atlassian/automation-manual-triggers';

import { AUTOMATION_SMART_BUTTON_RUN_RULE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { type ExperienceAttributes } from '@confluence/experience-tracker/entry-points/ExperienceEvent.ts';
import { type ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

export const i18n = defineMessages({
	ruleInvocationSuccessTitle: {
		id: 'automation-extensions.flag.invocation.success.title',
		defaultMessage: 'Your automation is in progress',
		description:
			'The title of the flag shown to the user when a manually triggered automation rule has successfully started.',
	},
	ruleInvocationFailureTitle: {
		id: 'automation-extensions.flag.invocation.failure.title',
		defaultMessage: 'Oops we ran into a problem',
		description:
			'The title of the flag shown to the user when a manually triggered automation fails to start.',
	},
	ruleInvocationFailureDescription: {
		id: 'automation-extensions.flag.invocation.failure.description',
		defaultMessage: 'There was a problem and we couldn’t run the automation',
		description:
			'The description of the flag shown to the user when a manually triggered automation fails to start.',
	},
});

const INVOCATIONS = 'invocations';

export const getFailedObjects = (response: InvocationResponse): string[] =>
	Object.keys(response).filter((object) => object != INVOCATIONS && response[object] !== 'SUCCESS');

export const onInvocationStart = (
	experienceTracker: ExperienceTrackerAPI,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
) => {
	createAnalyticsEvent({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'runAutomationSmartButton',
			source: 'viewPageScreen',
		},
	}).fire();
	experienceTracker.start({
		name: AUTOMATION_SMART_BUTTON_RUN_RULE,
	});
};

export const onInvocationSuccess = (
	flags: FlagsStateContainer,
	experienceTracker: ExperienceTrackerAPI,
): void => {
	void flags.showInfoFlag({
		id: 'automation-manual-trigger-execution-finished-flag',
		title: <FormattedMessage {...i18n.ruleInvocationSuccessTitle} />,
		isAutoDismiss: true,
	});
	experienceTracker.succeed({
		name: AUTOMATION_SMART_BUTTON_RUN_RULE,
	});
};

export const onInvocationFailure = (
	flags: FlagsStateContainer,
	experienceTracker: ExperienceTrackerAPI,
	error: Error,
	attributes?: ExperienceAttributes,
) => {
	void flags.showErrorFlag({
		id: 'automation-manual-trigger-execution-finished-flag',
		title: <FormattedMessage {...i18n.ruleInvocationFailureTitle} />,
		description: <FormattedMessage {...i18n.ruleInvocationFailureDescription} />,
		isAutoDismiss: true,
	});
	experienceTracker.fail({
		name: AUTOMATION_SMART_BUTTON_RUN_RULE,
		error,
		attributes,
	});
};
